var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo, useReducer, useRef } from 'react';
import { useAsyncEffect } from './useAsyncEffect';
const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCHING':
            return {
                status: 'loading',
                refreshing: state.refreshing,
            };
        case 'FETCHING_MORE':
            return {
                status: 'loading-more',
                refreshing: false,
            };
        case 'ERROR':
            return Object.assign(Object.assign({}, state), { status: 'error', error: action.payload, refreshing: false });
        case 'FETCH_SUCCESS':
            return {
                status: 'ready',
                refreshing: false,
            };
        case 'REFRESH':
            return Object.assign(Object.assign({}, state), { refreshing: true });
        default:
            return state;
    }
};
// fetchAssets can only error out on a network error
// if it were to error out, it would still return an empty list
// if a fetchLimit of 0 is provided, every asset associated with the assetStore is fetched
export const fetchSignedAssets = ({ assetStore, fetchLimit = 20, folderName, abortSignal, }) => __awaiter(void 0, void 0, void 0, function* () {
    const fetchedSignedAssets = yield assetStore.fetchAssets({
        limit: fetchLimit,
        folderName,
        // fetchAssets will completely fail if one asset fails to be presigned.  So lets not do that.
        presign: false,
        signal: abortSignal,
    });
    // for compatibility with how the presign flag works above we'll still await the presign
    // but we'll ignore any failures
    yield Promise.all(assetStore.assets.map((asset) => {
        try {
            return asset.presign();
        }
        catch (error) {
            return Promise.resolve();
        }
    }));
    return fetchedSignedAssets;
});
/**
 * Hook that returns a state object.
 * This object indicates the upload status, and depending on the status also exposes if the assetStore has more to fetch,
 * and a function to allow the user to fetch more
 */
export const useUploadedAssets = (params) => {
    const { fetchLimit, assetStore, onError, folderName } = params;
    const [state, dispatch] = useReducer(reducer, {
        status: 'idle',
        refreshing: false,
    });
    const fetchPromises = useRef(new Set());
    useAsyncEffect(({ runIfMounted, abortSignal }) => {
        const executeFetchSignedAssets = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!assetStore) {
                return;
            }
            try {
                dispatch({ type: 'FETCHING' });
                yield fetchSignedAssets({
                    assetStore,
                    // Suggested to select a fetch limit that is divisible by 2 and 3 - it will look better.
                    fetchLimit: fetchLimit !== undefined ? fetchLimit : 30,
                    folderName,
                    abortSignal,
                });
                runIfMounted(() => __awaiter(void 0, void 0, void 0, function* () {
                    dispatch({
                        type: 'FETCH_SUCCESS',
                    });
                }));
            }
            catch (error) {
                if (error instanceof Error && error.name === 'AbortError') {
                    return;
                }
                if (error instanceof Error) {
                    dispatch({ type: 'ERROR', payload: error });
                }
            }
        });
        executeFetchSignedAssets();
    }, [assetStore, fetchLimit, folderName, state.refreshing]);
    const refreshAssets = useCallback(() => {
        dispatch({ type: 'REFRESH' });
    }, [dispatch]);
    const processFetchMoreAssetsError = useCallback((err, errorMessage = 'Failed to fetch more assets') => {
        if (err instanceof Error) {
            dispatch({ type: 'ERROR', payload: err });
        }
        const error = Error(`${errorMessage}: ${err}`);
        if (onError) {
            onError(error);
        }
        else {
            throw error;
        }
    }, [onError]);
    const fetchMoreAssets = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (!assetStore) {
                throw new Error('AssetStore not available');
            }
            dispatch({ type: 'FETCHING_MORE' });
            // fetchMoreAssets will completely fail if one asset fails to be presigned.  Lets do better than that.
            yield assetStore.fetchMore({ presign: false });
            // presign everything ourselves.  Existing presigned assets will early exit
            // for compatibility with how the presign flag works above we'll still await the presign
            // but we'll ignore any failures
            yield Promise.all(assetStore.assets.map((asset) => {
                try {
                    return asset.presign();
                }
                catch (error) {
                    processFetchMoreAssetsError(error, 'Failed to fetch certain asset');
                    return Promise.resolve();
                }
            }));
            dispatch({
                type: 'FETCH_SUCCESS',
            });
        }
        catch (err) {
            processFetchMoreAssetsError(err);
        }
    }), [assetStore, processFetchMoreAssetsError]);
    const fetchMore = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        // wait for resolve previous ftch request.
        yield Promise.all([...fetchPromises.current]);
        // check if it's possibl to fetch more and there is no pending request
        if (!(assetStore === null || assetStore === void 0 ? void 0 : assetStore.canFetchMore) || fetchPromises.current.size) {
            return;
        }
        const fetchMorePromise = fetchMoreAssets();
        try {
            // add the fetch promise to the promises Set to prevent multiple requests in the same time
            fetchPromises.current.add(fetchMorePromise);
            yield fetchMorePromise;
        }
        finally {
            // remove the fetch promise from the promises Set when it's resolved or failed
            fetchPromises.current.delete(fetchMorePromise);
        }
    }), [assetStore === null || assetStore === void 0 ? void 0 : assetStore.canFetchMore, fetchMoreAssets]);
    return useMemo(() => {
        if (state.status === 'ready') {
            return Object.assign(Object.assign({}, state), { fetchMore,
                refreshAssets, canFetchMore: Boolean(assetStore === null || assetStore === void 0 ? void 0 : assetStore.canFetchMore) });
        }
        if (state.status === 'loading-more') {
            return Object.assign(Object.assign({}, state), { canFetchMore: Boolean(assetStore === null || assetStore === void 0 ? void 0 : assetStore.canFetchMore) });
        }
        return state;
    }, [state, fetchMore, refreshAssets, assetStore === null || assetStore === void 0 ? void 0 : assetStore.canFetchMore]);
};
//# sourceMappingURL=useUploadedAssets.js.map